import React, { useState } from "react";
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from "react-native";
import { observer } from "mobx-react";
import { pushStore } from "../stores/PushStore";
import { postStore } from "../stores/PostStore";
import { navigate } from "../services/NavigationService";
import { EnumRoutesAppWeb } from "../enum/EnumRoutesAppWeb";
import { PlusIcon, PencilSimpleIcon, TrashIcon, FunnelSimple as FilterIcon } from "@phosphor-icons/react";
import { Theme } from "../enum/Theme";
import SpaceW from "@comps/SpaceW";
import { NotificacaoType, ordemTiposNotificacao } from "@interfaces/notificationType";

const PushList = observer(() => {
  const { pushes, loading, deletePush } = pushStore;
  const { posts } = postStore;

  const [modalVisible, setModalVisible] = useState(false);
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [filterType, setFilterType] = useState<NotificacaoType | null>(null);

  const pushesFiltered = pushes.filter((push) => {
    const activeMatch = filterActive === null || push.active === filterActive;
    const typeMatch = filterType === null || push.type === filterType;
    return activeMatch && typeMatch;
  });

  const getPostTitle = (postId: string) => {
    const post = posts.find(p => p.id === postId);
    return post ? post.title : 'Post não encontrado';
  };

  const getTypeLabel = (type: NotificacaoType) => {
    const typeLabels: Record<NotificacaoType, string> = {
      'mamada_atrasada': 'Mamada Atrasada',
      'soneca_atrasada': 'Soneca Atrasada',
      'sono_noturno_atrasado': 'Sono Noturno Atrasado',
      'sono': 'Sono',
      'mamada': 'Mamada',
      'sono_noturno': 'Sono Noturno',
      'soneca': 'Soneca',
      'falta': 'Falta',
      'disciplina': 'Disciplina',
      'soneca_curta': 'Soneca Curta',
      'despertar_frequente': 'Despertar Frequente',
      'parabens': 'Parabéns',
      'reminder': 'Lembrete'
    };
    return typeLabels[type] || type;
  };

  if (loading) {
    return <Text style={{ textAlign: "center", marginTop: 50 }}>Carregando...</Text>;
  }

  return (
    <View style={styles.container}>
      <View style={styles.topBar}>
        <TouchableOpacity style={styles.addButton} onPress={() => navigate(EnumRoutesAppWeb.createEditPush)}>
          <PlusIcon size={20} color="#fff" />
          <Text style={styles.addButtonText}>Novo Push</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.filterButton} onPress={() => setModalVisible(true)}>
          <FilterIcon size={22} color={Theme.foreground} />
        </TouchableOpacity>
        {filterType && (
          <View style={styles.filterTag}>
            <Text style={styles.filterTagText}>{getTypeLabel(filterType)}</Text>
          </View>
        )}
      </View>

      <ScrollView>
        {pushesFiltered.map((push) => (
          <View style={styles.row} key={push.id}>
            <View style={styles.pushInfo}>
              <Text style={styles.pushTitle}>{push.title}</Text>
              <Text style={styles.pushSubtitle}>
                {getTypeLabel(push.type)} • {getPostTitle(push.post_id)} • {push.active ? 'Ativo' : 'Inativo'}
              </Text>
            </View>
            <SpaceW />
            <View style={styles.actions}>
              <TouchableOpacity onPress={() => navigate(EnumRoutesAppWeb.createEditPush, { push })}>
                <PencilSimpleIcon size={20} color="#555" />
              </TouchableOpacity>
              <SpaceW />
              <TouchableOpacity onPress={() => deletePush(push.id)}>
                <TrashIcon size={20} color="#E57373" />
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>

      {/* Modal de Filtro */}
      <View style={[styles.modal, { display: modalVisible ? 'flex' : 'none' }]}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>Filtros</Text>
          
          <Text style={styles.filterLabel}>Status:</Text>
          <View style={styles.filterButtons}>
            <TouchableOpacity 
              style={[styles.filterButton, filterActive === null && styles.filterButtonActive]}
              onPress={() => setFilterActive(null)}
            >
              <Text style={[styles.filterButtonText, filterActive === null && styles.filterButtonTextActive]}>Todos</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.filterButton, filterActive === true && styles.filterButtonActive]}
              onPress={() => setFilterActive(true)}
            >
              <Text style={[styles.filterButtonText, filterActive === true && styles.filterButtonTextActive]}>Ativos</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.filterButton, filterActive === false && styles.filterButtonActive]}
              onPress={() => setFilterActive(false)}
            >
              <Text style={[styles.filterButtonText, filterActive === false && styles.filterButtonTextActive]}>Inativos</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.filterLabel}>Tipo:</Text>
          <View style={styles.filterButtons}>
            <TouchableOpacity 
              style={[styles.filterButton, filterType === null && styles.filterButtonActive]}
              onPress={() => setFilterType(null)}
            >
              <Text style={[styles.filterButtonText, filterType === null && styles.filterButtonTextActive]}>Todos</Text>
            </TouchableOpacity>
            {ordemTiposNotificacao.map(type => (
              <TouchableOpacity 
                key={type}
                style={[styles.filterButton, filterType === type && styles.filterButtonActive]}
                onPress={() => setFilterType(type)}
              >
                <Text style={[styles.filterButtonText, filterType === type && styles.filterButtonTextActive]}>
                  {getTypeLabel(type)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity style={styles.closeButton} onPress={() => setModalVisible(false)}>
            <Text style={styles.closeButtonText}>Fechar</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
});

export default PushList;

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, backgroundColor: "#F2F2F2" },
  topBar: { flexDirection: "row", alignItems: "center", marginBottom: 15 },
  addButton: { backgroundColor: Theme.foreground, padding: 10, borderRadius: 5, flexDirection: "row", alignItems: "center" },
  addButtonText: { color: "#fff", marginLeft: 5 },
  filterButton: { marginLeft: 10, padding: 7, borderRadius: 5, backgroundColor: "#e6e6e6" },
  filterTag: { marginLeft: 10, padding: 5, borderRadius: 5, backgroundColor: Theme.foreground },
  filterTagText: { color: "#fff", fontSize: 12 },
  row: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: "#ccc" },
  pushInfo: { flex: 2 },
  pushTitle: { fontSize: 16, fontWeight: "bold", marginBottom: 4 },
  pushSubtitle: { fontSize: 12, color: "#666" },
  actions: { flexDirection: "row", gap: 10 },
  modal: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  modalContent: { backgroundColor: '#fff', padding: 20, borderRadius: 10, width: '90%', maxHeight: '80%' },
  modalTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 20, textAlign: 'center' },
  filterLabel: { fontSize: 16, fontWeight: 'bold', marginTop: 15, marginBottom: 10 },
  filterButtons: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 10 },
  filterButtonText: { color: "#333", fontSize: 12 },
  filterButtonTextActive: { color: "#fff" },
  filterButtonActive: { backgroundColor: Theme.foreground },
  closeButton: { backgroundColor: Theme.foreground, padding: 10, borderRadius: 5, marginTop: 20 },
  closeButtonText: { color: "#fff", textAlign: "center", fontWeight: "bold" },
});
